import {
  Footer,
  type Locale,
  type LocaleCodeType,
  OneColumnLayout,
  SimpleHeader,
} from "@pepperstone/pepper-ui-kit";
import type { CSSProperties, ReactNode } from "react";
import { useBackendVersion } from "./useBackendVersion";

const locales = [
  {
    code: "ar",
    label: "Arabic",
    displayCode: "AR",
  },
  {
    code: "de",
    label: "Deutsch",
    displayCode: "DE",
  },
  {
    code: "en",
    label: "English",
    displayCode: "EN",
  },
  {
    code: "es-419",
    label: "Español 419",
    displayCode: "ES-419",
  },
  {
    code: "es",
    label: "Español",
    displayCode: "ES-419",
  },
  {
    code: "fr",
    label: "Français",
    displayCode: "FR",
  },
  {
    code: "it",
    label: "Italiano",
    displayCode: "IT",
  },
  {
    code: "pl",
    label: "Polski",
    displayCode: "PL",
  },
  {
    code: "pt-BR",
    label: "Português BR",
    displayCode: "PT-BR",
  },
  {
    code: "th",
    label: "ไทย",
    displayCode: "TH",
  },
  {
    code: "vi",
    label: "Tiếng Việt",
    displayCode: "VI",
  },
  {
    code: "zh",
    label: "简体中文",
    displayCode: "ZH",
  },
  {
    code: "zh-TW",
    label: "繁体中文 TW",
    displayCode: "ZH-TW",
  },
] as Locale[];

type LayoutProps = {
  children: ReactNode;
  onLanguageChange: (locale: string) => void;
  currentLocale: string;
  isLoggedIn: boolean;
  isEmbeddedLayout: boolean;
  onLoginButtonClick?: () => void;
};

export const ContainerLayout = ({
  children,
  onLanguageChange,
  currentLocale,
  isLoggedIn,
  isEmbeddedLayout, // Temporary flag for mobile POC
  onLoginButtonClick,
}: LayoutProps) => {
  const beVersion = useBackendVersion();

  const pillStyle: CSSProperties = {
    background: "rgba(255, 255, 255, 0.15)",
    border: "1px solid rgba(255, 255, 255, 0.3)",
    color: "#ffffff",
    borderRadius: 100,
    fontSize: 10,
    fontFamily: "monospace",
    letterSpacing: "0.03em",
    padding: "2px 8px",
    pointerEvents: "none",
    userSelect: "none",
    whiteSpace: "nowrap",
  };

  return (
    <div style={{ minWidth: "100%", minHeight: "100vh" }}>
      <OneColumnLayout>
        {!isEmbeddedLayout && (
          <OneColumnLayout.Header>
            <SimpleHeader
              theme="dark"
              primaryBtnLabel={isLoggedIn ? "Logout" : "Login"}
              onPrimaryBtnClick={onLoginButtonClick}
              currentLocale={currentLocale as LocaleCodeType}
              locales={locales}
              isLanguageSelectorVisible={true}
              handleLanguageSelectorOnChange={(locale) =>
                onLanguageChange(locale.code)
              }
            />
            {import.meta.env.VITE_SHOW_VERSION_BADGE === "true" && (
              <div
                style={{
                  background: "rgba(26, 27, 27, 0.95)",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 8,
                  padding: "4px 16px",
                }}
              >
                <span style={pillStyle}>
                  FE {import.meta.env.VITE_COMMIT_HASH}
                </span>
                {beVersion && (
                  <span style={pillStyle}>BE {beVersion.commitHash}</span>
                )}
              </div>
            )}
          </OneColumnLayout.Header>
        )}

        <OneColumnLayout.Main>{children}</OneColumnLayout.Main>
        {!isEmbeddedLayout && (
          <OneColumnLayout.Footer>
            <Footer
              theme="dark"
              data={{
                contactEmail: "developers@pepperstone.com",
                companyAddress:
                  "Level 16, Tower One, 727 Colins Street <br/> Melbourne, VIC Australia 3008",
                contactPhoneNumbers: [
                  "1300 033 375",
                  "1300 033 376",
                  "1300 033 377",
                ],
              }}
              navigationLinkListHorizontal={
                isLoggedIn
                  ? [
                      { href: "/deposit", text: "Deposit" },
                      { href: "/withdraw", text: "Withdraw" },
                    ]
                  : [{ href: "/login", text: "Login" }]
              }
              legalTexts={[
                "This is a local development page please DO NOT use it in production",
              ]}
              linkList={[]}
              socialIconsList={[]}
            />
          </OneColumnLayout.Footer>
        )}
      </OneColumnLayout>
    </div>
  );
};
